interface Props {
  reason: "missing" | "malformed";
  rawKey: string | undefined;
  detectedViteKeys: string[];
  mode: string;
}

export default function MissingClerkKey({
  reason,
  rawKey,
  detectedViteKeys,
  mode,
}: Props) {
  const otherKeys = detectedViteKeys.filter(
    (k) => k !== "VITE_CLERK_PUBLISHABLE_KEY" && k !== "VITE_CLERK_KEY",
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-950 p-6 text-slate-100">
      <div className="surface w-full max-w-2xl p-8">
        <div className="text-xs uppercase tracking-wider text-amber-300">
          Configuration required
        </div>
        <h1 className="mt-2 text-2xl font-semibold text-white">
          Clerk is not configured.
        </h1>
        <p className="mt-3 text-slate-300">
          Add{" "}
          <code className="rounded bg-white/5 px-1.5 py-0.5 text-accent">
            VITE_CLERK_PUBLISHABLE_KEY
          </code>{" "}
          to{" "}
          <code className="rounded bg-white/5 px-1.5 py-0.5 text-accent">
            .env.local
          </code>{" "}
          in the project root and restart Vite.
        </p>

        <p className="mt-2 text-xs text-slate-400">
          {reason === "missing"
            ? "No publishable key was found in import.meta.env (checked VITE_CLERK_PUBLISHABLE_KEY and VITE_CLERK_KEY)."
            : `The key was set but doesn't match the pk_test_* / pk_live_* format${
                rawKey ? ` (got: "${rawKey.slice(0, 24)}…")` : ""
              }.`}
        </p>

        <div className="mt-5 grid gap-2 rounded-md border border-white/10 bg-ink-900/60 p-4 text-xs text-slate-300">
          <div className="text-slate-400">Vite startup state</div>
          <div>
            <span className="text-slate-400">mode:</span>{" "}
            <span className="text-slate-100">{mode}</span>
          </div>
          <div>
            <span className="text-slate-400">VITE_CLERK_PUBLISHABLE_KEY:</span>{" "}
            <span
              className={
                detectedViteKeys.includes("VITE_CLERK_PUBLISHABLE_KEY")
                  ? "text-emerald-300"
                  : "text-red-300"
              }
            >
              {detectedViteKeys.includes("VITE_CLERK_PUBLISHABLE_KEY")
                ? "loaded"
                : "not loaded"}
            </span>
          </div>
          <div>
            <span className="text-slate-400">VITE_CLERK_KEY (alias):</span>{" "}
            <span
              className={
                detectedViteKeys.includes("VITE_CLERK_KEY")
                  ? "text-emerald-300"
                  : "text-slate-500"
              }
            >
              {detectedViteKeys.includes("VITE_CLERK_KEY")
                ? "loaded"
                : "not loaded"}
            </span>
          </div>
          <div>
            <span className="text-slate-400">other VITE_* vars seen:</span>{" "}
            <span className="text-slate-100">
              {otherKeys.length > 0 ? otherKeys.join(", ") : "(none)"}
            </span>
          </div>
        </div>

        <pre className="mt-5 overflow-x-auto rounded-md border border-white/10 bg-ink-900 p-3 text-xs text-slate-200">
{`# .env.local  (Vite — exposed to the browser)
VITE_CLERK_PUBLISHABLE_KEY=pk_test_xxxxxxxxxxxxxxxxxxxxx

# .dev.vars  (Cloudflare Pages Functions — server-side)
CLERK_SECRET_KEY=sk_test_xxxxxxxxxxxxxxxxxxxxx
CLERK_JWT_ISSUER=https://your-frontend-api.clerk.accounts.dev
ALLOWED_ORIGIN=http://localhost:5173`}
        </pre>

        <ol className="mt-5 grid gap-2 text-sm text-slate-300">
          <li>
            <strong className="text-white">1.</strong> Confirm{" "}
            <code className="rounded bg-white/5 px-1 text-accent">.env.local</code>{" "}
            actually exists in the project root:
            <pre className="mt-1 overflow-x-auto rounded bg-ink-900 p-2 text-xs text-slate-200">
{`ls -la .env.local`}
            </pre>
            If you see "No such file or directory," the file isn't on disk —
            macOS Finder hides extensions by default, so files saved as{" "}
            <code className="text-accent">.env.local.txt</code> won't be picked
            up.
          </li>
          <li>
            <strong className="text-white">2.</strong> Verify there are no
            quotes, BOM, or trailing whitespace:
            <pre className="mt-1 overflow-x-auto rounded bg-ink-900 p-2 text-xs text-slate-200">
{`cat -A .env.local
file .env.local`}
            </pre>
            The line should be exactly{" "}
            <code className="text-accent">VITE_CLERK_PUBLISHABLE_KEY=pk_test_…</code>{" "}
            with no quotes and a trailing <code>$</code> (newline) shown by{" "}
            <code>cat -A</code>.
          </li>
          <li>
            <strong className="text-white">3.</strong> <strong>Restart Vite.</strong>{" "}
            Vite reads <code className="text-accent">.env.local</code> at
            startup, not on save. Stop the dev server with{" "}
            <kbd className="rounded border border-white/10 px-1">Ctrl</kbd>+
            <kbd className="rounded border border-white/10 px-1">C</kbd> and run:
            <pre className="mt-1 overflow-x-auto rounded bg-ink-900 p-2 text-xs text-slate-200">
{`npm run dev:clean   # clears node_modules/.vite and restarts`}
            </pre>
          </li>
          <li>
            <strong className="text-white">4.</strong> Refresh this tab. The
            "Vite startup state" box above should now show{" "}
            <span className="text-emerald-300">loaded</span> next to{" "}
            <code className="text-accent">VITE_CLERK_PUBLISHABLE_KEY</code>.
          </li>
        </ol>

        <p className="mt-5 text-xs text-slate-500">
          Open the browser console for a single startup log line with the same
          information.
        </p>
      </div>
    </div>
  );
}
